/* eslint-disable no-nested-ternary */
import { useEffect, useRef } from "react"
import Select, { components } from "react-select"
import castArray from "lodash/castArray"
import find from "lodash/find"
import join from "lodash/join"
import isEmpty from "lodash/isEmpty"
import * as PropTypes from "prop-types"
import styled from "styled-components"
import Typography from "components/styled/Typography"
import Icon from "components/icons/icon"
import { Checkbox } from "components/icons/checkbox"
import { CheckboxSelected } from "components/icons/checkboxSelected"
import { scrollToRef } from "helpers/scroll"

const Wrapper = styled.div`
  margin-bottom: ${({ filter }) => (filter ? "0" : "15px")};
`

const Item = styled.div`
  margin-right: 5px;
  :nth-child(n + 5) {
    display: none;
  }
`

const StyledCheckbox = styled(Icon)`
  margin-right: 5px;
  margin-right: 0.5rem;
`

const Label = styled(Typography)``

const Feedback = styled(Typography)`
  && {
    font-size: 1rem;
    width: 100%;
    margin: 0.25rem 0 0;
  }
`

export const getStyles = (filter, background, small) => {
  const mainColor = filter ? "#0b9ed9" : "#021859"
  return {
    control: (base, { hasValue, isFocused, isDisabled }) => ({
      ...base,
      border: `solid ${filter ? "2" : "1"}px ${isDisabled ? "#ACB5C6" : hasValue || isFocused ? mainColor : "#c3cde0"}`,
      color: isDisabled ? "#ACB5C6" : hasValue || isFocused ? mainColor : "#c3cde0",
      fontSize: "16px",
      lineHeight: "21px",
      fontWeight: "500",
      fontFamily: "Rubik, sans-serif",
      borderRadius: filter ? "12px" : "6px",
      background: isDisabled ? "#E6E8F0" : background,
      boxShadow: "none",
      padding: "9px 15px",
      position: "relative",
      marginBottom: "0",
      zIndex: isFocused ? "3" : "1",
      ":hover": { borderColor: mainColor },
      ".is-invalid &": {
        borderColor: "#E81E61",
        color: "#E81E61"
      }
    }),
    singleValue: (base, { hasValue }) => ({
      ...base,
      color: "inherit",
      fontSize: hasValue ? "21px" : "16px",
      lineHeight: hasValue ? "24px" : "21px",
      fontWeight: "500"
    }),
    multiValue: base => ({
      ...base,
      background: "transparent"
    }),
    multiValueContainer: base => ({
      ...base,
      display: "none",
      ":nth-child(n+2)": {
        display: "none"
      }
    }),
    multiValueLabel: base => ({
      ...base
    }),
    indicatorSeparator: base => ({ ...base, display: "none" }),
    multiValueRemove: base => ({
      ...base,
      display: "none"
    }),
    valueContainer: base => ({
      ...base,
      height: small ? "28px" : "40px",
      padding: 0
    }),
    container: base => ({ ...base, position: "relative" }),
    menu: base => ({
      ...base,
      top: "40px",
      position: "absolute",
      left: "0",
      right: "0",
      borderRadius: "0 0 16px 16px",
      padding: "10px 0 0",
      zIndex: "2",
      overflow: "hidden"
    }),
    menuList: base => ({
      ...base,
      overflowY: "scroll",
      maxHeight: "300px",
      "::-webkit-scrollbar": {
        display: "none"
      }
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      width: "100%",
      padding: "15px",
      background: isFocused || isSelected ? "#F7F7FA" : "#FFF",
      color: isFocused || isSelected ? mainColor : "#ACB5C6",
      fontSize: "16px",
      lineHeight: "21px",
      fontWeight: "400",
      fontFamily: "Rubik, sans-serif",
      fontStyle: isFocused || isSelected ? "normal" : "italic"
    }),
    placeholder: base => ({
      ...base,
      color: "#c3cde0",
      fontSize: "16px",
      lineHeight: "21px",
      fontWeight: "400",
      whiteSpace: "nowrap"
    }),
    dropdownIndicator: (base, { hasValue, isFocused }) => ({
      ...base,
      color: hasValue || isFocused ? mainColor : "#021859",
      padding: 0
    }),
    clearIndicator: base => ({ ...base, padding: 0 }),
    indicatorsContainer: base => ({ ...base, padding: 0 })
  }
}

export default function DropdownSelector(props) {
  const {
    className,
    filter,
    background,
    options,
    field,
    value,
    label,
    show,
    handleChange,
    placeholder,
    multiple,
    disabled,
    small,
    errors,
    dynamic
  } = props
  const ref = useRef(null)

  useEffect(() => {
    if (isEmpty(errors)) return
    const firstError = Object.keys(errors)[0]
    if (firstError === field) scrollToRef(ref)
  }, [errors])

  const errorsValue = errors && errors[field]
  const errorsText = Array.isArray(errorsValue) ? join(errorsValue) : errorsValue

  let val = value || (multiple ? [] : "")
  if (val) val = multiple ? castArray(val).map(x => find(options, { value: x })) : find(options, { value: val })

  if (!show) return null

  const onChange = x => {
    let res = multiple ? [] : ""
    if (x) res = multiple ? x.map(y => y.value) : x.value
    handleChange(field, res)
  }

  const enabledOptions = dynamic ? options.filter(x => !(x.disabled && !castArray(value).includes(x.value))) : options

  // TODO: Refactor - should not create this inside a component
  const MultiValueContainer = ({ data }) => {
    const name = data.label.replace(/ *\([^)]*\) */g, "")
    const pos = val.indexOf(data)
    let text = pos + 1 === val.length ? name : `${name}, `
    if (pos >= 3) text = `+ ${val.length - 3}`
    return (
      <Item className="Select-value" title={text}>
        <span className="Select-value-label">{text}</span>
      </Item>
    )
  }

  MultiValueContainer.propTypes = {
    data: PropTypes.object.isRequired
  }

  return (
    <Wrapper ref={ref} className={`${className} ${!isEmpty(errorsValue) ? "is-invalid" : ""}`} filter={+filter}>
      {label && (
        <Typography el="label" className="mb-2">
          {label}
        </Typography>
      )}
      <Select
        instanceId={field}
        field={field}
        value={val}
        onChange={onChange}
        options={enabledOptions}
        isSearchable={false}
        isClearable={false}
        captureMenuScroll
        placeholder={placeholder}
        styles={getStyles(filter, background, small)}
        isMulti={multiple}
        hideSelectedOptions={false}
        components={{ MultiValueContainer, Option }}
        isDisabled={disabled}
      />
      {errorsText && (
        <Feedback color="crimson" weight="medium">
          {errorsText}
        </Feedback>
      )}
    </Wrapper>
  )
}

const Option = props => {
  const { isSelected, label } = props
  return (
    <div>
      <components.Option {...props}>
        <StyledCheckbox Icon={isSelected ? CheckboxSelected : Checkbox} size="sm" focusable />
        <Label as="label" variant="label" color={isSelected ? "skyBlue" : "silver"} selected={isSelected}>
          {label}
        </Label>
      </components.Option>
    </div>
  )
}

Option.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired
}

DropdownSelector.defaultProps = {
  className: "",
  filter: false,
  background: "#fff",
  show: true,
  multiple: false,
  value: null,
  label: null,
  placeholder: "",
  disabled: false,
  errors: {},
  small: false,
  dynamic: false
}

DropdownSelector.propTypes = {
  className: PropTypes.string,
  filter: PropTypes.bool,
  background: PropTypes.string,
  show: PropTypes.bool,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  field: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  errors: PropTypes.object,
  small: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.any]),
  dynamic: PropTypes.bool
}
